import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { Picture } from '@aok/image'
import { CancelCircleFilled, CheckmarkCircleFilled } from '@aok/icon-functional'

import createRandomID from '../../../../utils/createRandomID'
import { DisabledState } from '../M047Constants'

/** A pruned variant of yggdrasil's version of <RadioButtonGroup/> with extra features:
 * - different visual disabled states to show error/success after question resolution
 * - extendable with custom elements before each radio button
 */
const M047RadioButtonGroup = ({
  groupName,
  groupLabel,
  radioButtons,
  defaultSelection,
  onChange,
  disabled,
  showImagePerOption,
  className,
  buttonOuterClasses,
}) => {
  const radioButtonId = useMemo(() => createRandomID('radio-button'), [])

  return (
    <fieldset className={`${className} radio-group flex flex-col`}>
      {/* Radio group label */}
      <legend className="sr-only">{groupLabel}</legend>

      <div className={buttonOuterClasses}>
        {radioButtons.map((radioButton, radioIndex) => {
          const radioKey = `${radioIndex}_${radioButton.value}`

          const radioBtnDisabledState = radioButton.disabledState ?? DisabledState.Default

          const radioBtnTextColor = classnames({
            // label color if disabled and error/success state or if not disabled
            'text-293033':
              (disabled &&
                (radioBtnDisabledState === DisabledState.Error || radioBtnDisabledState === DisabledState.Success)) ||
              !disabled,

            // label color if disabled and default/muted error state
            'text-586167':
              disabled &&
              (radioBtnDisabledState === DisabledState.ErrorMuted || radioBtnDisabledState === DisabledState.Default),
          })

          return (
            <div
              className={`w-full ${
                showImagePerOption
                  ? 'flex items-center gap-24 600:flex-col 600:gap-x-0 600:gap-y-16 600:justify-center 840:justify-start 840:gap-y-24'
                  : ''
              }`}
              key={radioKey}
            >
              {showImagePerOption && (
                <div className="flex-shrink-0 flex-grow-0 justify-self-start">
                  <Picture
                    {...radioButton.image}
                    imgClassName="w-80 h-80 rounded-full 600:w-112 600:h-112 1000:w-128  1000:h-128"
                  />
                </div>
              )}
              <input
                id={`${radioButtonId}-${radioIndex}`}
                className="sr-only"
                type="radio"
                value={radioButton.value}
                defaultChecked={defaultSelection === radioButton.value}
                name={groupName}
                required={radioButton.required}
                onChange={onChange}
                disabled={disabled}
                {...radioButton.props}
              />
              <label
                className={`
                  flex items-center justify-start py-4 min-h-32 w-auto rounded-6 cursor-pointer text-16 leading-24 1000:items-start 
                  ${showImagePerOption ? '600:justify-center' : ''}
                `}
                htmlFor={`${radioButtonId}-${radioIndex}`}
              >
                {!disabled || (disabled && radioBtnDisabledState === DisabledState.Default) ? (
                  <span className="radio-btn flex-shrink-0 border rounded-12 relative w-24 h-24 border-828b91 bg-ffffff left-4 flex items-center justify-center">
                    <span
                      className={`radio-btn-inner rounded-12 w-10 h-10
                      ${disabled ? 'bg-828b91' : 'bg-40484d'}
                    `}
                    />
                  </span>
                ) : disabled && radioBtnDisabledState === DisabledState.Error ? (
                  <div className="relative flex-shrink-0 border border-transparent left-4 w-24 h-24">
                    <CancelCircleFilled className="fill-current text-40484d w-full h-full" />
                  </div>
                ) : disabled && radioBtnDisabledState === DisabledState.Success ? (
                  <div className="relative flex-shrink-0 border border-transparent left-4 w-24 h-24">
                    <CheckmarkCircleFilled className="fill-current text-07983e w-full h-full" />
                  </div>
                ) : disabled && radioBtnDisabledState === DisabledState.ErrorMuted ? (
                  <div className="relative flex-shrink-0 border border-transparent left-4 w-24 h-24">
                    <CancelCircleFilled className="fill-current text-98a1a6 w-full h-full" />
                  </div>
                ) : null}

                <span className={`pr-4 flex items-center ml-12 ${radioBtnTextColor}`}>{radioButton.label}</span>
              </label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}

M047RadioButtonGroup.propTypes = {
  groupName: PropTypes.string.isRequired,
  groupLabel: PropTypes.string.isRequired,
  radioButtons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      props: PropTypes.object,
      disabledState: PropTypes.oneOf(Object.values(DisabledState)),
    })
  ),
  defaultSelection: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  showImagePerOption: PropTypes.bool,
  className: PropTypes.string,
  buttonOuterClasses: PropTypes.string,
}

M047RadioButtonGroup.defaultProps = {
  groupLabel: 'Label nicht gesetzt',
  radioButtons: [],
  onChange: () => {},
  disabled: false,
  className: '',
  buttonOuterClasses: '',
}

export default M047RadioButtonGroup
